import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  OnDestroy
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { TooltipService } from './tooltip.service';

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  @Input() text = '';
  @Input() maxWidth = '200px';

  @ViewChild('tooltipTemplate') tooltipTemplate!: TemplateRef<any>;
  @ViewChild('textEl', { static: true }) textEl!: ElementRef<HTMLElement>;

  private overlayRef!: OverlayRef;
  private outsideClickSub?: Subscription;
  private ro!: ResizeObserver;

  isMobile = false;
  isTruncated = false;

  constructor(
      private overlay: Overlay,
      private vcr: ViewContainerRef,
      private tooltipService: TooltipService
  ) {}

  ngAfterViewInit(): void {
    this.isMobile = window.matchMedia('(hover: none)').matches;
    this.ro = new ResizeObserver(() => this.checkTruncation());
    this.ro.observe(this.textEl.nativeElement);
    setTimeout(() => this.checkTruncation(), 0);
  }

  ngOnDestroy(): void {
    this.outsideClickSub?.unsubscribe();
    this.overlayRef?.dispose();
    this.ro.disconnect();
  }

  private checkTruncation(): void {
    const el = this.textEl.nativeElement;
    this.isTruncated = el.scrollWidth > el.clientWidth;
  }

  private createOverlay() {
    const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.textEl)
        .withPositions([
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom'
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top'
          },
        ])
        .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'cdk-tooltip-panel',
    });
  }

  private attachTooltip() {
    if (!this.isTruncated) return;

    if (!this.overlayRef) {
      this.createOverlay();
    }

    // Close any previously open tooltip
    this.tooltipService.open(this.overlayRef);
    const portal = new TemplatePortal(this.tooltipTemplate, this.vcr);
    this.overlayRef.attach(portal);

    if (this.isMobile) {
      this.outsideClickSub?.unsubscribe();
      this.outsideClickSub = fromEvent<MouseEvent>(document, 'click')
          .subscribe((event) => {
            const clickInside = this.textEl.nativeElement.contains(event.target as HTMLElement);
            if (!clickInside) {
              this.closeTooltip();
            }
          });
    }
  }

  private closeTooltip() {
    this.tooltipService.close(this.overlayRef);
    this.overlayRef?.detach();
    this.outsideClickSub?.unsubscribe();
  }

  onMouseEnter(): void {
    if (this.isMobile) return;
    this.attachTooltip();
  }

  onMouseLeave(): void {
    if (this.isMobile) return;
    this.closeTooltip();
  }

  onClick(event: MouseEvent): void {
    if (!this.isMobile) return;

    if (!this.isTruncated) return;

    if (this.overlayRef?.hasAttached()) {
      this.closeTooltip();
      return;
    }

    event.stopPropagation();
    this.attachTooltip();
  }
}
